import React from "react";
import {
  Box,
  Button,
  Flex,
  ScrollArea,
  SegmentedControl,
  Select,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { Order } from "@/shared/api/lod-api";
import { SortDirection, SourceOption } from "../lib/types";
import { createStageStatusOptions } from "@/features/assignment/lib";
import CustomDatePicker from "@/shared/ui/date-time-picker/date-time-picker";
import { SORT_DIRECTIONS } from "../lib/consts";

interface FilterSidebarProps {
  toDate: Date | null;
  source: Order["order_source"];
  orderId: string;
  assignee: string;
  fromDate: Date | null;
  isLoading: boolean;
  totalCount: number;
  sourceList: SourceOption[];
  assigneeList: SourceOption[];
  sortDirection: SortDirection;
  isFiltersApplied: boolean;
  stageStatusOptions: string[];
  stageJourneyAnnotation: string;
  stageTransactionAnnotation: string;
  stageTrackPersistenceAnnotation: string;
  applyFilters: () => void;
  clearFilters: () => void;
  onSourceChange: (value: string) => void;
  onToDateChange: (date: Date | null) => void;
  onOrderIdChange: (value: string) => void;
  onFromDateChange: (date: Date | null) => void;
  onAssigneeChange: (value: string) => void;
  onSortDirectionChange: (value: SortDirection) => void;
  onStageJourneyAnnotationChange: (value: string) => void;
  onStageTransactionAnnotationChange: (value: string) => void;
  onStageTrackPersistenceAnnotationChange: (value: string) => void;
}

export const FilterSection: React.FC<FilterSidebarProps> = ({
  toDate,
  source,
  orderId,
  assignee,
  fromDate,
  isLoading,
  totalCount,
  sourceList,
  assigneeList,
  sortDirection,
  isFiltersApplied,
  stageJourneyAnnotation,
  stageTransactionAnnotation,
  stageTrackPersistenceAnnotation,
  stageStatusOptions: apiStageStatusOptions,
  applyFilters,
  clearFilters,
  onSourceChange,
  onToDateChange,
  onOrderIdChange,
  onFromDateChange,
  onAssigneeChange,
  onSortDirectionChange,
  onStageJourneyAnnotationChange,
  onStageTransactionAnnotationChange,
  onStageTrackPersistenceAnnotationChange,
}) => {
  const stageStatusOptions = createStageStatusOptions(apiStageStatusOptions);

  return (
    <Box className="bg-white h-full">
      <ScrollArea className="h-[calc(100%-5rem)]" type="hover">
        <Flex direction="column" gap="2" className="pb-5 px-4 pt-1">
          <Flex direction="column" gap="2" mt="2">
            <Text weight="bold" as="p" size="3">
              Filter
            </Text>
            <Text size="2" weight="medium">
              Date Range
            </Text>
            <Flex justify="between" align="center">
              <Box width="40px">
                <Text size="2">From:</Text>
              </Box>
              <Flex className="flex-1">
                <CustomDatePicker
                  selected={fromDate}
                  onChange={onFromDateChange}
                />
              </Flex>
            </Flex>
            <Flex justify="between" align="center">
              <Box width="40px">
                <Text size="2">To:</Text>
              </Box>
              <Flex className="flex-1">
                <CustomDatePicker selected={toDate} onChange={onToDateChange} />
              </Flex>
            </Flex>
          </Flex>

          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Order ID
            </Text>
            <TextField.Root
              placeholder="Search by order ID"
              value={orderId}
              onChange={(e) => onOrderIdChange(e.target.value)}
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Order Source
            </Text>
            <Box>
              <Select.Root
                defaultValue="all"
                value={source}
                onValueChange={onSourceChange}
              >
                <Select.Trigger className="w-full" />
                <Select.Content>
                  <Select.Group>
                    <Select.Item value="all">All</Select.Item>
                    <Select.Separator />
                    {sourceList?.length
                      ? sourceList.map(({ key, value }) => (
                          <Select.Item key={key} value={key}>
                            {value}
                          </Select.Item>
                        ))
                      : null}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </Box>
          </Box>
          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Current Assignee
            </Text>
            <Box>
              <Select.Root
                defaultValue="all"
                value={assignee}
                onValueChange={onAssigneeChange}
              >
                <Select.Trigger className="w-full" />
                <Select.Content
                  onWheel={(e) => {
                    // A workaround to resolve scrolling issue https://github.com/radix-ui/primitives/issues/1159
                    e.stopPropagation();
                  }}
                >
                  <Select.Group>
                    <Select.Item value="all">All</Select.Item>
                    <Select.Separator />
                    {assigneeList?.length
                      ? assigneeList.map(({ key, value }) => (
                          <Select.Item key={key} value={key}>
                            {value}
                          </Select.Item>
                        ))
                      : null}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </Box>
          </Box>

          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Sort
            </Text>
            <Box>
              <SegmentedControl.Root
                className="w-full"
                defaultValue={SORT_DIRECTIONS.DESC}
                value={sortDirection}
                onValueChange={onSortDirectionChange}
              >
                <SegmentedControl.Item value={SORT_DIRECTIONS.DESC}>
                  Newest
                </SegmentedControl.Item>
                <SegmentedControl.Item value={SORT_DIRECTIONS.ASC}>
                  Oldest
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Box>
          </Box>

          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Stage Journey Annotation
            </Text>
            <Select.Root
              value={stageJourneyAnnotation || ""}
              onValueChange={(value) => onStageJourneyAnnotationChange(value)}
            >
              <Select.Trigger className="w-full"></Select.Trigger>
              <Select.Content>
                <Select.Item value="all">All</Select.Item>
                <Select.Separator />
                {stageStatusOptions?.map((option) => (
                  <Select.Item key={option.key} value={option.key}>
                    {option.value}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Stage Transaction Annotation
            </Text>
            <Select.Root
              value={stageTransactionAnnotation || ""}
              onValueChange={(value) =>
                onStageTransactionAnnotationChange(value)
              }
            >
              <Select.Trigger className="w-full"></Select.Trigger>
              <Select.Content>
                <Select.Item value="all">All</Select.Item>
                <Select.Separator />
                {stageStatusOptions?.map((option) => (
                  <Select.Item key={option.key} value={option.key}>
                    {option.value}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          <Box>
            <Text as="p" size="2" weight="medium" className="mb-1">
              Stage Track Persistence Annotation
            </Text>
            <Select.Root
              value={stageTrackPersistenceAnnotation || ""}
              onValueChange={(value) =>
                onStageTrackPersistenceAnnotationChange(value)
              }
            >
              <Select.Trigger className="w-full"></Select.Trigger>
              <Select.Content>
                <Select.Item value="all">All</Select.Item>
                <Select.Separator />
                {stageStatusOptions?.map((option) => (
                  <Select.Item key={option.key} value={option.key}>
                    {option.value}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        </Flex>
      </ScrollArea>
      <Separator size="4" />
      <Flex direction="column" gap="2" className="px-4 pt-2">
        <Flex gap="2">
          <Button onClick={clearFilters} variant="outline" className="flex-1">
            Clear
          </Button>
          <Button
            onClick={applyFilters}
            disabled={isFiltersApplied}
            className="flex-1"
          >
            Apply
          </Button>
        </Flex>

        {!isLoading && (
          <Text size="2" className="text-center">
            Found Items: {totalCount}
          </Text>
        )}
      </Flex>
    </Box>
  );
};
